/**
 * GovernmentCipSection — entity-registration CIP fields for Government entities.
 *
 * Per government.md §"Identity / CIP semantics":
 *
 *   - Field 254 (IdVerifyCode): default 8 (Other) — conservative per
 *     builder-rules. Bill's card suggests 6 (State Documents) for PublicFunds
 *     but flags it as "Pending Bill clarification".
 *     TODO(bill): government walkthrough needed to finalize Field 254 per sub-type.
 *   - Field 255 (ID Number): blank (no personal ID); placeholder for future
 *     entity registration # if policy changes.
 *   - Field 256 (ID Issuer / Jurisdiction): government jurisdiction (state
 *     or federal authority that issued the enabling resolution).
 *   - Field 257 (Issue Date): blank per card default.
 *   - Field 258 (Expiration Date): blank per card default.
 *
 * Upload slots surfaced in this section:
 *   - Hard-required: Finance Officer Authorization  (`FINANCE_OFFICER_AUTH`)
 *   - Soft-required: Governing Body Resolution    (`GOVT_BANKING_RES`)
 *
 * NO CIP scan panel — government entity has no photo ID. Individual signers
 * (authorized finance officers) have their own Individual CIF records
 * captured via separate Individual dialogs (not here).
 *
 * Fields live on `person.extra.cipField254` / `.cipField255` / etc. until
 * Wave-3 lifts them to first-class RaoPerson keys.
 */

import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Pill } from '@/components/primitives';
import type { RaoPerson } from '../../../types';
import type { AttachedDoc } from '../shared/attachedDocs';
import {
  CIP_FIELD_254_DEFAULTS,
  FINANCE_OFFICER_AUTH_DOC_CODE,
  GOVT_BANKING_RES_DOC_CODE,
} from './government-config';
import type { GovernmentSubType } from './government-config';

// -----------------------------------------------------------------------------
// Leaf styles (consistent with Individual sections)
// -----------------------------------------------------------------------------

const labelStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-600)',
  display: 'block',
  marginBottom: 4,
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 4,
  border: '1px solid var(--ink-200)',
  fontSize: 14,
  boxSizing: 'border-box',
};

const readOnlyStyle: CSSProperties = {
  ...inputStyle,
  background: 'var(--field-readonly-bg, #f5f5f5)',
  color: 'var(--ink-700)',
};

const sectionHeaderStyle: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.8,
  color: 'var(--pcb-blue)',
  marginBottom: 8,
};

const slotRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  alignItems: 'center',
  gap: 12,
  padding: '10px 12px',
  border: '1px solid var(--ink-100)',
  borderRadius: 8,
  background: 'var(--surface-card, #fff)',
};

function changedFieldStyle(base: CSSProperties, changed: boolean): CSSProperties {
  return changed
    ? { ...base, borderLeft: '3px solid var(--pcb-blue, #2968ad)', background: 'var(--field-changed-bg, #f0f7ff)', color: 'var(--ink-900, #1d1d1f)' }
    : base;
}

// -----------------------------------------------------------------------------
// Extra-field helpers — CIP fields live on `person.extra` for now.
// -----------------------------------------------------------------------------

function readExtra(person: RaoPerson, key: string): string {
  const v = person.extra?.[key];
  return typeof v === 'string' ? v : '';
}

function extraPatch(person: RaoPerson, patch: Record<string, unknown>): Partial<RaoPerson> {
  return { extra: { ...(person.extra ?? {}), ...patch } };
}

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

export interface GovernmentCipSectionProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  mode: 'new' | 'existing';
  isChanged?: (field: string) => boolean;
  governmentSubType: GovernmentSubType;
  /** Dialog-level attached-document store (scan / upload / linked-existing). */
  attachedDocs?: ReadonlyArray<AttachedDoc>;
  /** Called when an upload lands here; parent lifts into the shared store. */
  onDocumentRegistered?: (doc: AttachedDoc) => void;
  readOnly?: boolean;
}

// -----------------------------------------------------------------------------
// ID Type options (Core Director Field 254)
// -----------------------------------------------------------------------------

const ID_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '0', label: '0 · None' },
  { value: '6', label: '6 · State Documents' },
  { value: '7', label: '7 · Birth Certificate' },
  { value: '8', label: '8 · Other (enabling resolution)' },
];

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function GovernmentCipSection({
  person,
  onChange,
  mode: _mode,
  isChanged,
  governmentSubType,
  attachedDocs,
  onDocumentRegistered,
  readOnly = false,
}: GovernmentCipSectionProps): JSX.Element {
  const ic = isChanged ?? ((_: string) => false);

  // TODO(bill): government walkthrough needed — confirm 254 default per
  // sub-type. Seeding with conservative default from government-config.
  const field254Default = CIP_FIELD_254_DEFAULTS[governmentSubType] ?? '8';
  const field254 = readExtra(person, 'cipField254') || field254Default;
  const field255 = readExtra(person, 'cipField255');
  const field256 = readExtra(person, 'cipField256');
  const field257 = readExtra(person, 'cipField257');
  const field258 = readExtra(person, 'cipField258');

  // Push the 254 default into person.extra on first render so downstream
  // save path sees an authoritative value (not empty string).
  useEffect(() => {
    if (!person.extra || typeof (person.extra as Record<string, unknown>)['cipField254'] !== 'string') {
      onChange(extraPatch(person, { cipField254: field254Default }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [governmentSubType]);

  const updateField = (key: string, value: string): void => {
    onChange(extraPatch(person, { [key]: value }));
  };

  // Upload-slot fulfillment lookup (from dialog-level attachedDocs)
  const slotLookup = useMemo(() => {
    const map = new Map<string, AttachedDoc>();
    for (const d of attachedDocs ?? []) {
      map.set(d.docCode, d);
    }
    return map;
  }, [attachedDocs]);

  const handleSlotUpload = (docCode: string, file: File): void => {
    if (readOnly) return;
    // Stage as an 'upload' AttachedDoc — DocumentSlotsSection (bottom of
    // dialog) will push the actual upload via the Function route. Here we
    // only register a local-preview entry so the slot UI shows "uploaded".
    const objectUrl = URL.createObjectURL(file);
    onDocumentRegistered?.({
      docCode,
      source: 'upload',
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      imageDataUrl: file.type.startsWith('image/') ? objectUrl : '',
      fileUrl: objectUrl,
      capturedAt: new Date().toISOString(),
    });
  };

  const financeOfficerAuthFulfilled = slotLookup.get(FINANCE_OFFICER_AUTH_DOC_CODE);
  const govtBankingResFulfilled = slotLookup.get(GOVT_BANKING_RES_DOC_CODE);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={sectionHeaderStyle}>CIP — Entity Registration</div>

      {/* Info banner — government entity has no photo ID */}
      <div
        style={{
          padding: '10px 14px',
          background: 'var(--info-bg)',
          border: '1px solid var(--pcb-blue)',
          borderRadius: 6,
          fontSize: 12,
          color: 'var(--ink-900)',
        }}
      >
        <strong>Entity-level CIP.</strong> Government entities are identified
        by their enabling resolution (not photo ID). Authorized finance officer
        signers are captured as separate Individual CIF records.
      </div>

      {/* TODO(bill): government walkthrough needed — the 5 fields below are
          skeleton-level defaults per government.md §"Identity / CIP semantics".
          Walkthrough should confirm Field 254 value per sub-type + labelling. */}
      {/* Field 254 (ID Verify Code) + Field 256 (Jurisdiction) row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-gov-cip-254">
            ID Type (Field 254)
          </label>
          <select
            id="rao-gov-cip-254"
            value={field254}
            onChange={(e) => updateField('cipField254', e.currentTarget.value)}
            disabled={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('cipField254'))}
            title="Core Director Field 254"
          >
            {ID_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 2 }}>
            Conservative default: <strong>8 (Other)</strong>.
            {/* TODO(bill): walkthrough to confirm 6 vs 8 for PublicFunds */}
          </div>
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-gov-cip-256">
            Issuing Jurisdiction (Field 256)
          </label>
          <input
            id="rao-gov-cip-256"
            type="text"
            value={field256}
            onChange={(e) => updateField('cipField256', e.currentTarget.value)}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('cipField256'))}
            placeholder="e.g., State of Missouri, Phelps County"
            maxLength={40}
            title="Government jurisdiction that issued the enabling resolution."
          />
        </div>
      </div>

      {/* Field 255 (ID Number) + 257 (Issue Date) + 258 (Expiration) row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-gov-cip-255">
            Registration # (Field 255)
          </label>
          <input
            id="rao-gov-cip-255"
            type="text"
            value={field255}
            onChange={(e) => updateField('cipField255', e.currentTarget.value)}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('cipField255'))}
            placeholder="Usually blank"
            maxLength={20}
            title="Typically blank for government entities."
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-gov-cip-257">
            Issue Date (Field 257)
          </label>
          <input
            id="rao-gov-cip-257"
            type="date"
            value={field257}
            onChange={(e) => updateField('cipField257', e.currentTarget.value)}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('cipField257'))}
            title="Enabling-resolution date (typically blank)."
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-gov-cip-258">
            Expiration (Field 258)
          </label>
          <input
            id="rao-gov-cip-258"
            type="date"
            value={field258}
            onChange={(e) => updateField('cipField258', e.currentTarget.value)}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('cipField258'))}
            title="Typically blank for government entities."
          />
        </div>
      </div>

      {/* Hard-required upload slot — Finance Officer Authorization */}
      <div style={sectionHeaderStyle}>Required Uploads</div>
      <UploadSlot
        docCode={FINANCE_OFFICER_AUTH_DOC_CODE}
        label="Finance Officer Authorization"
        requirementLevel="Hard"
        fulfilled={financeOfficerAuthFulfilled}
        onUpload={handleSlotUpload}
        readOnly={readOnly}
        note="Signed authorization letter or resolution naming the finance officer of record."
      />
      <UploadSlot
        docCode={GOVT_BANKING_RES_DOC_CODE}
        label="Governing Body Resolution"
        requirementLevel="Soft"
        fulfilled={govtBankingResFulfilled}
        onUpload={handleSlotUpload}
        readOnly={readOnly}
        note="Banking resolution adopted by the governing body (council / board / commission)."
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Upload slot — inline variant of DocumentSlotsSection's row for the
// 2 CIP-entry uploads that live in this section. Kept local to avoid
// dragging the whole catalog-driven slot grid up into the CIP area.
// -----------------------------------------------------------------------------

interface UploadSlotProps {
  docCode: string;
  label: string;
  requirementLevel: 'Hard' | 'Soft' | 'Recommended';
  fulfilled: AttachedDoc | undefined;
  onUpload: (docCode: string, file: File) => void;
  readOnly: boolean;
  note?: string;
}

function UploadSlot({
  docCode,
  label,
  requirementLevel,
  fulfilled,
  onUpload,
  readOnly,
  note,
}: UploadSlotProps): JSX.Element {
  const badge =
    requirementLevel === 'Hard' ? (
      <Pill variant="err">Hard · Required</Pill>
    ) : requirementLevel === 'Soft' ? (
      <Pill variant="warn">Soft · Follow-up</Pill>
    ) : (
      <Pill variant="muted">Recommended</Pill>
    );

  return (
    <div style={slotRowStyle}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-900)' }}>
            {label}
          </span>
          {badge}
          {fulfilled?.source === 'upload' && <Pill variant="ok">Uploaded</Pill>}
        </div>
        {fulfilled ? (
          <div style={{ fontSize: 11, color: 'var(--ink-600)' }}>
            {fulfilled.filename}
            {fulfilled.sizeBytes > 0 && (
              <span> · {formatBytes(fulfilled.sizeBytes)}</span>
            )}
          </div>
        ) : (
          <div style={{ fontSize: 11, color: 'var(--ink-600)' }}>
            Not yet uploaded.
          </div>
        )}
        {note && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink-500)',
              marginTop: 2,
              fontStyle: 'italic',
            }}
          >
            {note}
          </div>
        )}
      </div>
      <div>
        <label
          style={{
            display: 'inline-block',
            fontSize: 12,
            fontWeight: 600,
            color: fulfilled ? 'var(--pcb-blue)' : 'var(--on-brand)',
            background: fulfilled ? 'transparent' : 'var(--brand-fill)',
            border: fulfilled ? '1px solid var(--pcb-blue)' : 'none',
            cursor: readOnly ? 'not-allowed' : 'pointer',
            padding: fulfilled ? '4px 10px' : '6px 12px',
            borderRadius: 6,
          }}
        >
          {fulfilled ? 'Replace' : '📎 Upload'}
          <input
            type="file"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.currentTarget.files?.[0];
              if (f) onUpload(docCode, f);
            }}
            disabled={readOnly}
          />
        </label>
      </div>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}
